import { useEffect } from 'react'
import { weekdayName } from '@app/shared'
import clsx from 'clsx'

import { useClock, useDesktopWorker } from '../../lib/data'
import { durationPhrase } from '../../lib/time'
import { timerLabel, useMemoRecorder, WAVEFORM_BARS } from '../lib/useMemoRecorder'

// ============================================================================
// 1d · VOICE MEMO — docs/design/handoff-mobile/README.md §1d.
//
// "Record, and make the wait finite." A full-screen dark surface — "the only
// dark in-app screen; it exists so the screen is unreadable in a lecture hall
// and the affordance is unmistakable". It is dark in BOTH themes for that
// reason, which is why every colour here comes from the invariant `rec-*` tokens
// rather than from `ink`/`surface`, which invert.
//
// THE WORKER IS NAMED FROM THE API, NEVER FROM THE MOCKUP. `KAZ-DESKTOP ·
// OFFLINE` is fixture text; what renders here is `worker.machine` and
// `worker.state` off `/api/worker-status`, and the queue rows are that endpoint's
// own `queue` — derived entirely from `audio_jobs`, with `state` only ever
// 'online' when a job is provably claimed right now. A queued memo naming a
// machine that is not the owner's would be the app inventing a computer.
//
// ── TWO AFFORDANCES THE MOCKUP DRAWS AND THIS SCREEN DOES NOT ───────────────
// "Wake by Wake-on-LAN" and "Tag" are both absent rather than dead. There is no
// WoL endpoint and no memo-tagging write in the data contract, and the product
// commitment that outranks any visual detail is that anything the upstream API
// cannot do is ABSENT from the UI, not shown as an empty affordance — the same
// rule the README states outright for the Study button on 1f. Both are named in
// the port report.
// ============================================================================

export function RecordOverlay({
  open,
  held = true,
  onClose,
}: {
  open: boolean
  /**
   * Is a finger holding the recording? True for the composer's hold-to-record circle; false when
   * it was started from the + menu's Voice memo (V10 board 03), where nothing is held and the
   * memo is saved with the stop button. Only the one instruction line differs.
   */
  held?: boolean
  /** The finger came up, or the memo finished. Either way the surface goes. */
  onClose: () => void
}) {
  const { data: clock } = useClock()
  const recorder = useMemoRecorder(clock?.date, held)

  const { start, reset, phase } = recorder

  // The surface and the microphone open together: this component is mounted by
  // the hold gesture, so mounting IS the start signal.
  useEffect(() => {
    if (!open) return
    reset()
    start()
    // `start` and `reset` are stable callbacks; re-running on their identity
    // would restart the microphone mid-memo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // A finished upload closes the surface on its own. The memo row on Today is
  // where it lives from here, and it is already `queued` there.
  useEffect(() => {
    if (phase === 'saved') onClose()
  }, [phase, onClose])

  if (!open) return null

  const day = clock ? weekdayName(clock.date, false) : 'today'
  const recording = phase === 'recording'

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col bg-rec-ground"
      role="dialog"
      aria-modal="true"
      aria-label="Recording a voice memo"
      style={{
        paddingTop: 'env(safe-area-inset-top, 0px)',
        paddingBottom: 'env(safe-area-inset-bottom, 24px)',
      }}
    >
      <div className="flex min-h-0 flex-1 flex-col items-center gap-[26px] px-6 pt-[34px]">
        <div className="flex items-center gap-[9px]">
          <span
            className={clsx(
              'size-[9px] rounded-full',
              recording ? 'bg-danger-lift' : 'bg-rec-bar-4',
            )}
            aria-hidden
          />
          <span
            aria-live="polite"
            className={clsx(
              'text-[10.5px] font-semibold uppercase leading-none tracking-[0.1em]',
              recording ? 'text-danger-lift' : 'text-rec-ink-3',
            )}
          >
            {phase === 'starting' && 'Opening the microphone'}
            {recording && 'Recording'}
            {phase === 'uploading' && 'Uploading'}
            {phase === 'failed' && 'Not recording'}
            {phase === 'idle' && 'Ready'}
            {phase === 'saved' && 'Saved'}
          </span>
        </div>

        <div className="text-[62px] font-light leading-none tracking-[-0.03em] text-on-dark tabular-nums">
          {timerLabel(recorder.elapsed)}
        </div>

        <Waveform levels={recorder.levels} active={recording} />

        {recorder.problem ? (
          <p className="max-w-[300px] text-center text-[13.5px] leading-[1.6] text-danger-lift text-pretty">
            {recorder.problem}
          </p>
        ) : (
          // COPY DEVIATION, DELIBERATE AND FLAGGED. The mockup says "It records
          // with the screen off and uploads when you have signal." A PWA cannot
          // promise the first half — Android suspends a backgrounded tab's
          // MediaRecorder when the screen goes off, and the native app of Phase 9
          // is what makes that sentence true. Never fake knowledge outranks "use
          // the exact strings from the mockup"; the rest of the line is verbatim.
          <p className="max-w-[300px] text-center text-[13.5px] leading-[1.6] text-rec-ink-3 text-pretty">
            {held ? 'Release' : 'Tap stop'} to save into {day}’s entry. It uploads when you have
            signal — keep this screen open while it records.
          </p>
        )}

        <WorkerQueue />
      </div>

      <div className="flex flex-none items-center justify-center gap-[26px] px-6 pb-[18px]">
        {/* UNDO is real: it stops the recorder and throws the audio away. The
            mockup's third control ("Tag") is absent — see the header. */}
        <button
          type="button"
          onClick={() => {
            recorder.discard()
            onClose()
          }}
          className="flex size-[52px] items-center justify-center rounded-full border border-rec-line-2 text-[12.5px] font-medium leading-none text-rec-ink-3 active:bg-rec-line"
        >
          Undo
        </button>

        <button
          type="button"
          aria-label="Stop and save this memo"
          onClick={() => recorder.stopAndSave()}
          className="mo-press flex size-[82px] items-center justify-center rounded-full bg-on-dark shadow-stop"
        >
          <span className="size-[26px] rounded-keycap bg-rec-ground" aria-hidden />
        </button>

        {/* The right-hand control's 52px slot is kept so the stop button stays
            centred on the thumb, which is the whole geometry of this row. */}
        <span className="size-[52px] flex-none" aria-hidden />
      </div>
    </div>
  )
}

/**
 * Twenty bars, 3px wide, radius 2, heights 14–92px, ramping quietest to loudest
 * with the loudest white — README §1d, and "Animate from real input levels".
 *
 * A bar at rest keeps its minimum height rather than collapsing to nothing: a
 * meter that disappears when the room is quiet is indistinguishable from a meter
 * that has stopped working.
 */
function Waveform({ levels, active }: { levels: number[]; active: boolean }) {
  return (
    <div className="flex h-24 w-full items-center justify-center gap-[3px]" aria-hidden>
      {Array.from({ length: WAVEFORM_BARS }, (_, i) => {
        const level = active ? (levels[i] ?? 0) : 0
        return (
          <span
            key={i}
            className={clsx(
              'w-[3px] rounded-[2px] transition-[height] duration-[var(--dur-fast)] ease-[var(--ease-out)]',
              BAR_TONE[Math.min(BAR_TONE.length - 1, Math.floor(level * BAR_TONE.length))],
            )}
            style={{ height: `${Math.round(14 + level * 78)}px` }}
          />
        )
      })}
    </div>
  )
}

/** The six-step ramp, quietest to loudest. Written out in full — the scanner cannot read a template. */
const BAR_TONE = [
  'bg-rec-bar-1',
  'bg-rec-bar-2',
  'bg-rec-bar-3',
  'bg-rec-bar-4',
  'bg-rec-bar-5',
  'bg-rec-bar-6',
]

/**
 * THE WORKER QUEUE CARD — what makes the wait finite.
 *
 * Everything in it is `/api/worker-status`: the machine's real name, whether a
 * job is provably claimed right now, and the jobs in the order the PC will run
 * them. The numbered rows are the queue's own order, which is what turns "it is
 * being transcribed somewhere" into "it is third".
 */
function WorkerQueue() {
  const { data: worker, isPending, error } = useDesktopWorker()

  if (isPending) {
    return (
      <div className="mb-[22px] mt-auto h-[168px] w-full animate-pulse rounded-context bg-rec-card" aria-hidden />
    )
  }

  if (error || !worker) {
    return (
      <div className="mb-[22px] mt-auto w-full rounded-context border border-rec-line bg-rec-card px-4 py-[15px]">
        <p className="text-[13px] leading-[1.6] text-rec-ink-2 text-pretty">
          The transcription queue could not be read, so this screen cannot say how many memos are
          waiting or which machine they are waiting on. The recording itself is unaffected — it
          uploads either way.
        </p>
      </div>
    )
  }

  const offline = worker.state === 'offline'
  const waiting = worker.queue.length

  return (
    <div className="mb-[22px] mt-auto flex w-full flex-col gap-3 rounded-context border border-rec-line bg-rec-card px-4 py-[15px]">
      <div className="flex items-center gap-2">
        <span
          className={clsx('size-[7px] rounded-full', offline ? 'bg-warning-dot' : 'bg-live-dot')}
          aria-hidden
        />
        <span
          className={clsx(
            'text-[10px] font-semibold uppercase leading-none tracking-[0.08em]',
            offline ? 'text-rec-warn' : 'text-live-dot',
          )}
        >
          {worker.machine} · {worker.state}
        </span>
      </div>

      <p className="text-[13px] leading-[1.6] text-rec-ink-2 text-pretty">
        {waiting === 0
          ? offline
            ? `Nothing is waiting. This memo will be the first thing ${worker.machine} runs when it wakes up.`
            : `Nothing is waiting, and ${worker.machine} is awake — this memo goes straight in.`
          : `${waiting} ${waiting === 1 ? 'memo is' : 'memos are'} waiting for your PC. Nothing is lost — they run in order the next time it wakes up.`}
      </p>

      {waiting > 0 && (
        <ol className="flex flex-col gap-[7px]">
          {worker.queue.map((job, i) => (
            <li key={job.id} className="flex items-center gap-[10px]">
              <span className="w-[14px] flex-none text-[10px] font-semibold leading-none text-rec-ink-4">
                {String(i + 1).padStart(2, '0')}
              </span>
              <span className="min-w-0 flex-1 truncate text-[12.5px] font-medium leading-[1.3] text-rec-ink">
                {job.title} · {durationPhrase(job.durationSec)}
              </span>
              <span className="flex-none rounded-pill bg-rec-line px-[7px] py-[2px] text-[9px] font-semibold uppercase leading-[1.5] tracking-[0.06em] text-rec-ink-3">
                {job.work}
              </span>
            </li>
          ))}
        </ol>
      )}
    </div>
  )
}
